import React from 'react';

const getItemTitle = (item) => {
  const isAzucarGlass = Boolean(item?.isAzucarGlass);
  const diffThreshold = isAzucarGlass ? 0.01 : 0.1;
  const decimals = isAzucarGlass ? 2 : 1;
  if (item.quantity > 0 && Math.abs(item.quantityExact - item.quantity) > diffThreshold) {
    return `Pronóstico exacto: ${item.quantityExact.toFixed(decimals)} ${item.unitLabel || 'unidades'}`;
  }
  return item.lifespan || '';
};

const ProductionSummary = ({ data, onAdjustCategory, onSelectItem }) => {
  if (!data || !data.groups) {
    return (
      <div className="summary-empty">
        <div className="summary-empty-icon">🧠</div>
        <div className="summary-empty-text">Genera un pronóstico</div>
        <div className="summary-empty-subtext">Configura los parámetros para visualizar recomendaciones</div>
      </div>
    );
  }

  if (data.groups.length === 0) {
    return (
      <div className="summary-empty">
        <div className="summary-empty-icon">✅</div>
        <div className="summary-empty-text">¡Todo en stock!</div>
        <div className="summary-empty-subtext">No hay artículos para producir en este momento</div>
      </div>
    );
  }

  const formatStockout = (item) => {
    if (!item.stockout) {
      return { text: 'Sin datos de tiempo', status: 'warning' };
    }
    if (item.stock <= 0) {
      return { text: 'Captura tu stock', status: 'warning' };
    }
    if (item.stockout.totalMinutes >= 168 * 60) {
      return { text: 'Dura +7 días', status: 'ok', icon: 'check-circle' };
    }
    if (item.stockout.totalMinutes <= 120) {
      return { text: `Se agota en ${item.stockout.formattedTime}`, status: 'critical', icon: 'alert-circle' };
    }
    if (item.stockout.totalMinutes <= 360) {
      return { text: `Se agota en ${item.stockout.formattedTime}`, status: 'warning', icon: null };
    }
    return { text: `Dura ${item.stockout.formattedTime}`, status: 'ok', icon: 'check' };
  };

  const handleAdjustClick = (group) => {
    if (typeof onAdjustCategory === 'function') {
      onAdjustCategory({
        key: group.key,
        type: data.isRecipeProcess ? 'linkedArticle' : 'category',
        name: group.name
      });
      return;
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('legacy-adjust-category', {
        detail: {
          key: group.key,
          type: data.isRecipeProcess ? 'linkedArticle' : 'category',
          name: group.name
        }
      }));
    }
  };

  return (
    <>
      {data.groups.map((group, groupIndex) => (
        <div
          className="category-group entering"
          style={{ animationDelay: `${groupIndex * 0.2}s` }}
          key={group.key || group.name}
        >
          <div className="category-header">
            <div className="category-header-content">
              <div className="category-info">
                <h3 className={`category-title ${group.adjustment ? 'has-adjustment' : ''}`}>
                  {group.name}
                  {group.adjustment ? ` (${group.adjustment.percentage > 0 ? '+' : ''}${group.adjustment.percentage}%)` : ''}
                </h3>
              </div>
              <button
                className={`btn-adjust-forecast ${group.adjustment ? 'has-adjustment' : ''}`}
                onClick={() => handleAdjustClick(group)}
                title={`Ajustar pronóstico para ${group.name}`}
              >
                <i data-lucide="trending-up" />
                Ajustar pronóstico
              </button>
            </div>
          </div>
          {/* Banner when group has multiple azucar glass recipes */}
          {(() => {
            const recipeItems = group.items.filter((item) => item.isRecipe && item.totalRecipes > 1);
            const totalRecipes = recipeItems.length > 0 ? recipeItems[0].totalRecipes : 0;
            return totalRecipes > 1 ? (
              <div className="azucar-recipes-banner">
                <span className="azucar-recipes-banner-icon">
                  <i data-lucide="layers" />
                </span>
                <span className="azucar-recipes-banner-text">
                  Se requieren <strong>{totalRecipes} recetas</strong> para cubrir la demanda
                </span>
              </div>
            ) : null;
          })()}
          <div className="category-items-grid">
            {group.items.map((item, itemIndex) => {
              const stockout = item.isAzucarGlass ? null : formatStockout(item);
              const quantityDisplay = item.isAzucarGlass
                ? (Number.isFinite(item.quantity) ? item.quantity.toFixed(2) : '0.00')
                : (item.quantity > 0 ? item.quantity : 'Sin Prod');
              const unitDisplay = item.isAzucarGlass
                ? (item.unitLabel || 'UNIDADES')
                : (item.quantity > 0 ? (item.unitLabel || 'UNIDADES') : 'SUFICIENTE');
              return (
                <div
                  className={`summary-item ${item.status || ''} ${item.isRecipe ? 'summary-item-recipe' : ''} entering`}
                  style={{ animationDelay: `${groupIndex * 0.2 + itemIndex * 0.05}s` }}
                  key={`${group.name}-${item.name}`}
                  onClick={item.isAzucarGlass ? undefined : (() => onSelectItem?.(item))}
                >
                  {/* Recipe badge in top-right corner */}
                  {item.isRecipe && (
                    <div className="recipe-badge">
                      <span className="recipe-badge-number">#{item.recipeIndex + 1}</span>
                    </div>
                  )}
                  {item.imagen_url ? (
                    <img src={item.imagen_url} alt={item.name} className="summary-item-image" />
                  ) : (
                    <div className="summary-item-image-placeholder" />
                  )}
                  {/* For recipe cards: show base name as title, recipe counter as subtitle */}
                  {item.isRecipe ? (
                    <>
                      <div className="summary-item-name">{item.baseName || item.name}</div>
                      {item.totalRecipes > 1 && (
                        <div className="recipe-counter">
                          Receta {item.recipeIndex + 1} de {item.totalRecipes}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="summary-item-name">{item.name}</div>
                      {item.baseName ? (
                        <div className="summary-item-subtitle">Base: {item.baseName}</div>
                      ) : null}
                    </>
                  )}
                  {/* Hide main quantity for recipe cards (moved to ingredients) */}
                  {!item.isRecipe && (
                    <>
                      <div className="summary-item-value" title={getItemTitle(item)}>
                        {quantityDisplay}
                      </div>
                      <div className="summary-item-label">{unitDisplay}</div>
                    </>
                  )}
                  {!item.isAzucarGlass && stockout ? (
                    <div className={`summary-item-stockout ${stockout.status}`}>
                      {stockout.icon ? <i data-lucide={stockout.icon} /> : null}
                      {stockout.text}
                    </div>
                  ) : null}
                  {Array.isArray(item.ingredients) && item.ingredients.length ? (
                    <div className={`summary-item-ingredients ${item.isRecipe ? 'summary-item-ingredients-hero' : ''}`}>
                      <div className="summary-item-ingredients-title">Ingredientes</div>
                      <ul className="summary-item-ingredients-list">
                        {item.ingredients.map((ingredient) => (
                          <li
                            className={`summary-item-ingredient ${ingredient.isBase ? 'summary-item-ingredient-base' : ''}`}
                            key={`${item.name}-${ingredient.name}`}
                          >
                            <span className="summary-item-ingredient-name">{ingredient.name}</span>
                            <span className="summary-item-ingredient-qty">
                              {ingredient.displayQuantity} {ingredient.unitLabel}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {/* Forecast Period */}
                  <div className="forecast-period-section">
                    {item.periodData?.isBlocked ? (
                      <div className="forecast-period-blocked">
                        <span className="forecast-period-blocked-text">Horario limitado</span>
                      </div>
                    ) : item.periodData ? (
                      <>
                        <div
                          className="forecast-period-display"
                          title={item.periodTooltip || item.periodData.rangeText}
                        >
                          <span className="forecast-period-text">{item.periodData.rangeText}</span>
                        </div>
                        {item.periodData.hoursText && (
                          <div className="forecast-hours-display">
                            <span className="forecast-hours-text">{item.periodData.hoursText}</span>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};

export default ProductionSummary;
